/**
 * Phase 45 Plan 45-06 — single project group within the MyTasks list.
 *
 * Renders the sticky project header + N MyTaskRow children. Collapse state
 * (Petra UAT 2026-05-19) persists per project id in sessionStorage so the
 * preference survives navigation within the session but resets on browser
 * reopen — matches the usePreferences sessionStorage convention.
 *
 * Phase 86 Plan 86-06 — forwards `viewportMode` (the responsive collapse),
 * `cursorTaskId` (the keyboard cursor row) and `registerHandle` (the
 * `T`-shortcut touch trigger) straight through to each child `MyTaskRow`,
 * unchanged — project mode is one of the four sort modes the keyboard layer
 * and the responsive collapse both have to cover, not just the default rot
 * view.
 */
import { useCallback, useState } from 'react';
import type { MyTaskDto } from '../../services/api/tasks';
import { MyTasksProjectHeader } from './MyTasksProjectHeader';
import { MyTaskRow, type Density, type MyTaskRowHandle, type MyTasksViewportMode } from './MyTaskRow';

export interface ProjectGroupData {
  projectId: number;
  projectName: string;
  customerColorHex?: string | null;
  isSla?: boolean;
  tasks: MyTaskDto[];
}

export interface MyTasksProjectGroupProps {
  group: ProjectGroupData;
  density: Density;
  onRowClick?: (taskId: number) => void;
  viewportMode?: MyTasksViewportMode;
  cursorTaskId?: number | null;
  registerHandle?: (taskId: number, handle: MyTaskRowHandle | null) => void;
}

function storageKey(projectId: number): string {
  return `mytasks.collapsed.${projectId}`;
}

function readCollapsed(projectId: number): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(storageKey(projectId)) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(projectId: number, collapsed: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (collapsed) {
      window.sessionStorage.setItem(storageKey(projectId), '1');
    } else {
      window.sessionStorage.removeItem(storageKey(projectId));
    }
  } catch {
    // sessionStorage unavailable (private browsing edge) — silent fallback.
  }
}

export function MyTasksProjectGroup({
  group,
  density,
  onRowClick,
  viewportMode,
  cursorTaskId,
  registerHandle,
}: MyTasksProjectGroupProps) {
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed(group.projectId));

  const handleToggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      writeCollapsed(group.projectId, next);
      return next;
    });
  }, [group.projectId]);

  return (
    <section aria-label={`Project ${group.projectName}`} data-testid="my-tasks-project-group">
      <MyTasksProjectHeader
        projectId={group.projectId}
        projectName={group.projectName}
        taskCount={group.tasks.length}
        customerColorHex={group.customerColorHex}
        isSla={group.isSla}
        collapsed={collapsed}
        onToggleCollapsed={handleToggle}
      />
      {!collapsed &&
        group.tasks.map((t, idx) => (
          <MyTaskRow
            key={t.taskId}
            task={t}
            density={density}
            isSla={group.isSla}
            onRowClick={onRowClick}
            rowIndex={idx}
            viewportMode={viewportMode}
            isCursor={cursorTaskId === t.taskId}
            registerHandle={registerHandle}
          />
        ))}
    </section>
  );
}
